/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
import { makeStyles } from 'tss-react/mui';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ClearIcon from '@mui/icons-material/Clear';
import { createFilterOptions } from '@mui/material/useAutocomplete';

import { getIn } from 'formik';
import HintText from 'components/labels/HintText';
import { CircularProgress, List, ListItem, ListItemText } from '@mui/material';

const filter = createFilterOptions();
const getOptionLabel = (option) => option.name;

const useStyles = makeStyles()((theme) => ({
  menuContainer: {
    position: 'absolute',
    zIndex: 200,
    width: '100%',
    top: '55px',
    left: '8px',
  },
  noResultsContainer: {
    padding: theme.spacing(1),
  },
  inputField: {
    margin: theme.spacing(1),
  },
  root: {
    position: 'relative',
  },
}));

const areItemsDifferentChecker = (prevItem, item) => {
  return (
    (prevItem == null && item != null) ||
    (prevItem != null && item == null) ||
    (prevItem != null && item != null && prevItem.id !== item.id)
  );
};

// COMPONENT
function FormikDownshiftComplete({
  label,
  placeholder,
  field,
  form: { touched, errors, setFieldValue },
  dataSource,
  getSuggestionLabel,
  onChange,
  noOptionsNode,
  isLoading,
}) {
  const { classes } = useStyles();

  const touch = getIn(touched, field.name);
  const error = getIn(errors, field.name);

  return (
    <Downshift
      selectedItemChanged={areItemsDifferentChecker}
      onChange={(selectedItem) => {
        setFieldValue(field.name, selectedItem);
        if (onChange) {
          onChange(field.name, selectedItem);
        }
      }}
      selectedItem={field.value?.id != null ? field.value : null}
      itemToString={(item) => (item ? getSuggestionLabel(item) : '')}
      disabled
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        isOpen,
        selectedItem,
        clearSelection,
        openMenu,
        closeMenu,
        inputValue: currentInputValue,
        highlightedIndex,
        getRootProps,
      }) => {
        const suggestions = isOpen
          ? filter(dataSource, { inputValue: currentInputValue, getOptionLabel })
          : null;

        return (
          <div {...getRootProps({}, { suppressRefError: true })} className={classes.root}>
            <TextField
              variant="standard"
              label={label}
              placeholder={placeholder}
              InputLabelProps={{
                ...getLabelProps({ shrink: true }),
              }}
              InputProps={{
                ...getInputProps({
                  disabled: isLoading,
                  onKeyDown: (event) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      event.stopPropagation();
                    }
                  },
                  onFocus: () => {
                    openMenu();
                  },
                  autoComplete: 'off',
                  endAdornment:
                    selectedItem != null ? (
                      <InputAdornment position="end">
                        <IconButton aria-label="Remove Item" onClick={clearSelection} size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Expand/Collapse Dropdown"
                          onClick={() => {
                            if (isOpen) {
                              closeMenu();
                            } else {
                              openMenu();
                            }
                          }}
                        >
                          {isLoading && <CircularProgress size={20} thickness={5} />}
                          {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                }),
              }}
              error={touch && error != null}
              helperText={touch && error != null ? (error?.id != null ? error.id : error) : null}
              className={classes.inputField}
              fullWidth
            />

            {isOpen ? (
              <Paper elevation={2} className={classes.menuContainer}>
                <List {...getMenuProps()} dense>
                  {suggestions?.length === 0 && noOptionsNode && (
                    <div className={classes.noResultsContainer}>
                      {noOptionsNode(currentInputValue, selectedItem, closeMenu)}
                    </div>
                  )}
                  {suggestions?.length === 0 && !noOptionsNode && (
                    <HintText className={classes.noResultsContainer}>
                      Nenhum resultado. {currentInputValue.trim().length === 0 && ' Digita para buscar...'}
                    </HintText>
                  )}
                  {suggestions?.length > 0 &&
                    suggestions.map((suggestion, index) => {
                      const isSelected = selectedItem != null && selectedItem.id === suggestion.id;
                      return (
                        <ListItem
                          {...getItemProps({
                            key: suggestion.id,
                            index,
                            item: suggestion,
                            style: {
                              backgroundColor: highlightedIndex === index ? 'lightgray' : 'white',
                              fontWeight: isSelected ? 'bold' : 'normal',
                            },
                          })}
                          selected={isSelected}
                          button
                        >
                          <ListItemText primary={getSuggestionLabel(suggestion)} />
                        </ListItem>
                      );
                    })}
                </List>
              </Paper>
            ) : null}
          </div>
        );
      }}
    </Downshift>
  );
}

FormikDownshiftComplete.defaultProps = {
  label: null,
  placeholder: 'Selecione...',
  onChange: null,
  getSuggestionLabel: (object) => {
    return object.name;
  },
};
FormikDownshiftComplete.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  getSuggestionLabel: PropTypes.func.isRequired,
  onChange: PropTypes.func,
};

export default FormikDownshiftComplete;
